import {
  Pagination as PaginationNav,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import type { Paged } from "@/lib/paging";

interface PaginationProps {
  page: Pick<
    Paged<unknown>,
    "pageNumber" | "pageSize" | "totalCount" | "totalPages" | "hasPreviousPage" | "hasNextPage"
  >;
  onPageChange: (pageNumber: number) => void;
  busy?: boolean;
  className?: string;
}

type PageToken = number | "ellipsis";

function pageTokens(current: number, total: number): PageToken[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  const tokens: PageToken[] = [1];
  if (left > 2) tokens.push("ellipsis");
  for (let i = left; i <= right; i++) tokens.push(i);
  if (right < total - 1) tokens.push("ellipsis");
  tokens.push(total);
  return tokens;
}

export function Pagination({ page, onPageChange, busy, className }: PaginationProps) {
  const { pageNumber, pageSize, totalCount, totalPages, hasPreviousPage, hasNextPage } = page;

  const first = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const last = Math.min(pageNumber * pageSize, totalCount);

  const go = (n: number) => {
    if (busy || n < 1 || n > totalPages || n === pageNumber) return;
    onPageChange(n);
  };

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t border-slate-200",
        className,
      )}
    >
      <span className="text-xs text-slate-500">
        {totalCount === 0 ? "No results" : `Showing ${first}–${last} of ${totalCount}`}
      </span>

      {totalPages > 1 && (
        <PaginationNav className="mx-0 w-auto justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => { e.preventDefault(); go(pageNumber - 1); }}
                aria-disabled={busy || !hasPreviousPage}
                className={cn((busy || !hasPreviousPage) && "pointer-events-none opacity-50")}
              />
            </PaginationItem>

            {pageTokens(pageNumber, totalPages).map((tok, i) =>
              tok === "ellipsis" ? (
                <PaginationItem key={`e${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={tok}>
                  <PaginationLink
                    href="#"
                    isActive={tok === pageNumber}
                    onClick={(e) => { e.preventDefault(); go(tok); }}
                    className={cn(busy && "pointer-events-none opacity-50")}
                  >
                    {tok}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => { e.preventDefault(); go(pageNumber + 1); }}
                aria-disabled={busy || !hasNextPage}
                className={cn((busy || !hasNextPage) && "pointer-events-none opacity-50")}
              />
            </PaginationItem>
          </PaginationContent>
        </PaginationNav>
      )}
    </div>
  );
}
